/**
 * Sidebar Menu System - Section Group Based
 * Works with sidebar-menu-group sections to create dynamic menus
 */

class SidebarMenuSystem {
    constructor() {
      this.currentPageHandle = null;
      this.assignedMenuHandle = null;
      this.menuData = null;
      this.isMobile = window.innerWidth <= 768;
      
      // Listen for resize events to handle responsive behavior
      window.addEventListener('resize', () => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= 768;
        
        // Recreate layout if screen size category changed
        if (wasMobile !== this.isMobile && this.menuData) {
          this.updateLayout();
        }
      });
      
      // Optimized initialization
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.waitForMenuSystem());
      } else {
        // DOM is already ready, start immediately
        setTimeout(() => this.waitForMenuSystem(), 0);
      }
    }
  
      waitForMenuSystem() {
    // Try immediate check first for faster loading
    if (window.SIDEBAR_MENU_SYSTEM && Object.keys(window.SIDEBAR_MENU_SYSTEM.menus).length > 0) {
      //this.debugLog('⚡ Menu system ready immediately');
      this.init();
      return;
    }

    const maxAttempts = 10; // Reduced attempts for faster timeout
    let attempts = 0;

    const checkSystem = () => {
      attempts++;
      
      if (window.SIDEBAR_MENU_SYSTEM && Object.keys(window.SIDEBAR_MENU_SYSTEM.menus).length > 0) {
        //this.debugLog('✅ Menu system loaded with', Object.keys(window.SIDEBAR_MENU_SYSTEM.menus).length, 'menus');
        this.init();
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(checkSystem, 100); // Faster checks
      } else {
        //this.debugLog('❌ Menu system timeout after', maxAttempts * 100, 'ms');
      }
    };

    checkSystem();
  }
  
    init() {
      //this.debugLog('🚀 Initializing Sidebar Menu System');
      
      // Get current page handle
      this.currentPageHandle = this.getCurrentPageHandle();
      //this.debugLog('📄 Current page handle:', this.currentPageHandle);
      
      if (!this.currentPageHandle) {
        //this.debugLog('❌ No page handle detected - not a /pages/ URL');
        return;
      }
  
      // Check if this page has a menu assigned
      this.assignedMenuHandle = window.SIDEBAR_MENU_SYSTEM.pageMenuMap[this.currentPageHandle];
      
      if (!this.assignedMenuHandle) {
        //this.debugLog('❌ No menu assigned to page:', this.currentPageHandle);
        return;
      }
  
      // Get menu data
      this.menuData = window.SIDEBAR_MENU_SYSTEM.menus[this.assignedMenuHandle];
      
      if (!this.menuData) {
        //this.debugLog('❌ Menu data not found for:', this.assignedMenuHandle);
        return;
      }
  
      //this.debugLog('🎯 Menu found:', this.assignedMenuHandle, this.menuData);
      this.createSidebarLayout();
    }
  
    getCurrentPageHandle() {
      const path = window.location.pathname;
      const segments = path.split('/').filter(segment => segment.length > 0);
      
      if (segments.length >= 2 && segments[0] === 'pages') {
        return segments[1];
      }
      
      return null;
    }
  
        createSidebarLayout() {
      //this.debugLog('🎨 Creating sidebar layout for', this.isMobile ? 'mobile' : 'desktop');

      // Get main content container
      const mainContent = this.findMainContent();
      if (!mainContent) {
        //this.debugLog('❌ Could not find main content container');
        return;
      }

      //this.debugLog('🎯 Target container:', mainContent.className, mainContent.tagName);

      // Check if layout already exists
      if (document.querySelector('.sidebar-menu-layout')) {
        //this.debugLog('⚠️ Sidebar layout already exists on page');
        return;
      }

      // Skip if this looks like a hero section or banner
      if (this.isHeroSection(mainContent)) {
        //this.debugLog('⚠️ Skipping hero/banner section');
        return;
      }

      // Use requestAnimationFrame for smooth rendering
      requestAnimationFrame(() => {
        if (this.isMobile) {
          this.createMobileLayout(mainContent);
        } else {
          this.createDesktopLayout(mainContent);
        }
      });
    }

    createDesktopLayout(mainContent) {
      // Create layout wrapper with proper flex styling
      const layoutWrapper = document.createElement('div');
      layoutWrapper.className = 'sidebar-menu-layout desktop-layout';
      layoutWrapper.style.cssText = `
        display: flex !important;
        gap: 50px;
        align-items: flex-start;
        width: 100%;
        box-sizing: border-box;
      `;

      // Create sidebar
      const sidebar = this.createSidebar();
      
      // Create content area with explicit flex properties
      const contentArea = document.createElement('div');
      contentArea.className = 'sidebar-menu-content';
      contentArea.style.cssText = `
        flex: 1;
        min-width: 0;
        width: auto;
        box-sizing: border-box;
      `;
      
      // Move existing content while preserving structure
      const existingContent = Array.from(mainContent.children);
      existingContent.forEach(child => {
        contentArea.appendChild(child);
      });

      // Assemble layout
      layoutWrapper.appendChild(sidebar);
      layoutWrapper.appendChild(contentArea);

      // Replace main content and ensure parent has proper display
      mainContent.innerHTML = '';
      mainContent.appendChild(layoutWrapper);
      
      // Ensure parent container supports flex layout
      mainContent.style.display = 'block';
      mainContent.style.width = '100%';

      //this.debugLog('✅ Desktop sidebar layout created successfully');
    }

    createMobileLayout(mainContent) {
      // For mobile, keep content as-is and add navigation at the end
      const contentWrapper = document.createElement('div');
      contentWrapper.className = 'sidebar-menu-layout mobile-layout';
      contentWrapper.style.cssText = `
        width: 100%;
        box-sizing: border-box;
      `;

      // Move existing content to wrapper
      const existingContent = Array.from(mainContent.children);
      existingContent.forEach(child => {
        contentWrapper.appendChild(child);
      });

      // Create mobile navigation as last section
      const mobileNav = this.createMobileNavigation();

      // Replace main content and add navigation at the end
      mainContent.innerHTML = '';
      mainContent.appendChild(contentWrapper);
      mainContent.appendChild(mobileNav);

      //this.debugLog('✅ Mobile layout with end navigation created successfully');
    }

    updateLayout() {
      // Remove existing layout
      const existingLayout = document.querySelector('.sidebar-menu-layout');
      const existingMobileNav = document.querySelector('.sidebar-menu-mobile-nav');
      
      if (existingLayout) {
        const content = existingLayout.querySelector('.sidebar-menu-content') || existingLayout;
        const mainContent = this.findMainContent();
        
        if (mainContent && content) {
          // Move content back to main container
          Array.from(content.children).forEach(child => {
            mainContent.appendChild(child);
          });
        }
        
        existingLayout.remove();
      }
      
      // Remove mobile nav whether it's in body or main content
      if (existingMobileNav) {
        existingMobileNav.remove();
      }

      // Recreate with new layout
      this.createSidebarLayout();
    }
  
    findMainContent() {
      // First, look for the specific sidebar menu target
      const sidebarTarget = document.querySelector('#sidebarmenu');
      if (sidebarTarget) {
        //this.debugLog('🎯 Found specific sidebar target: #sidebarmenu');
        return sidebarTarget;
      }
  
      // Fallback: Look for main page content structure
      const pageContentSelectors = [
        '.page-width.page-content',
        '.page-content',
        'section.shopify-section .page-width',
        'section[id*="main"] .page-width'
      ];
  
      for (const selector of pageContentSelectors) {
        const element = document.querySelector(selector);
        if (element) {
          //this.debugLog('🎯 Found page content container:', selector);
          return element;
        }
      }
  
      //this.debugLog('❌ Could not find suitable content container');
      //this.debugLog('💡 Either add <div id="sidebarmenu"></div> or ensure .page-content exists');
      return null;
    }
  
        createSidebar() {
      const sidebar = document.createElement('nav');
      sidebar.className = 'sidebar-menu-nav desktop-sidebar';
      
      // Apply custom styling
      sidebar.style.cssText = `
        width: ${this.menuData.menuWidth}%;
        min-width: 200px;
        background-color: ${this.menuData.backgroundColor};
        color: ${this.menuData.textColor};
      `;

      let sidebarHTML = '';

      // Add title if enabled and has content
      if (this.menuData.showTitle && (this.menuData.title || this.menuData.icon || this.menuData.iconImage)) {
        const menuIcon = this.getIconHTML(this.menuData.icon, this.menuData.iconImage, 'sidebar-menu-icon');
        sidebarHTML += `
          <h3 class="sidebar-menu-title" style="color: ${this.menuData.textColor};">
            ${menuIcon}
            ${this.menuData.title || ''}
          </h3>
        `;
      }

      // Add menu items
      sidebarHTML += '<ul class="sidebar-menu-items">';
      
      this.menuData.items.forEach(item => {
        const isActive = this.isItemActive(item);
        const activeStyle = isActive ? `background-color: ${this.menuData.activeColor}; color: white;` : '';
        const itemIcon = this.getIconHTML(item.icon, item.iconImage, 'sidebar-menu-item-icon');
        
        sidebarHTML += `
          <li class="sidebar-menu-item">
            <a href="${item.url}" 
               class="sidebar-menu-link ${isActive ? 'active' : ''}"
               style="color: ${isActive ? 'white' : this.menuData.textColor}; ${activeStyle}"
               data-page-handle="${item.pageHandle}">
              ${itemIcon}
              <span class="sidebar-menu-item-text">${item.title}</span>
            </a>
          </li>
        `;
      });

      sidebarHTML += '</ul>';
      sidebar.innerHTML = sidebarHTML;

      return sidebar;
    }

    createMobileNavigation() {
      const mobileNav = document.createElement('nav');
      mobileNav.className = 'sidebar-menu-mobile-nav';
      
      // Apply mobile navigation styling (as regular section, not fixed)
      mobileNav.style.cssText = `
        width: 100%;
        background-color: ${this.menuData.backgroundColor};
        border-top: 1px solid #e0e0e0;
        padding: 20px 0 24px 0;
        margin-top: 40px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
      `;

      // Create mobile menu items container (max 3 columns)
      let mobileHTML = '<div class="sidebar-menu-mobile-items" style="';
      mobileHTML += 'display: grid; ';
      mobileHTML += `grid-template-columns: repeat(${Math.min(this.menuData.items.length, 3)}, 1fr); `;
      mobileHTML += 'gap: 12px; ';
      mobileHTML += 'max-width: 1200px; ';
      mobileHTML += 'margin: 0 auto; ';
      mobileHTML += 'padding: 0 20px; ';
      mobileHTML += 'box-sizing: border-box;">';
      
      this.menuData.items.forEach(item => {
        const isActive = this.isItemActive(item);
        const itemIcon = this.getIconHTML(item.icon, item.iconImage, 'sidebar-menu-mobile-icon');
        
        mobileHTML += `
          <a href="${item.url}" 
             class="sidebar-menu-mobile-item ${isActive ? 'active' : ''}"
             style="
               display: flex;
               flex-direction: column;
               align-items: center;
               text-decoration: none;
               color: ${isActive ? 'white' : this.menuData.textColor};
               background: ${isActive ? this.menuData.activeColor : '#f8f8f8'};
               border-radius: 12px;
               padding: 12px 8px;
               transition: all 0.3s ease;
               border: 2px solid ${isActive ? this.menuData.activeColor : 'transparent'};
               min-height: 80px;
               justify-content: center;
               text-align: center;
               box-sizing: border-box;
             "
             data-page-handle="${item.pageHandle}">
            <div class="mobile-icon" style="
              width: 32px;
              height: 32px;
              background: ${isActive ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 8px;
              font-size: 16px;
              transition: all 0.3s ease;
            ">
              ${itemIcon}
            </div>
            <span class="mobile-text" style="
              font-size: 11px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              line-height: 1.2;
              word-break: break-word;
              hyphens: auto;
            ">${item.title}</span>
          </a>
        `;
      });

      mobileHTML += '</div>';
      mobileNav.innerHTML = mobileHTML;

      return mobileNav;
    }
  
    isItemActive(item) {
      // Check if current page matches this item
      if (item.pageHandle && item.pageHandle === this.currentPageHandle) {
        return true;
      }
      
      // Fallback: check if current URL matches
      if (item.url && window.location.pathname === item.url) {
        return true;
      }
  
      return false;
    }
  
    isHeroSection(element) {
      // Check if this element looks like a hero/banner section
      const heroKeywords = ['hero', 'banner', 'slideshow', 'carousel', 'slider'];
      const className = element.className.toLowerCase();
      const dataType = element.getAttribute('data-section-type') || '';
      
      for (const keyword of heroKeywords) {
        if (className.includes(keyword) || dataType.includes(keyword)) {
          //this.debugLog('🚫 Detected hero section keyword:', keyword);
          return true;
        }
      }
  
      // Check if it has a large background image (likely hero)
      const style = window.getComputedStyle(element);
      if (style.backgroundImage && style.backgroundImage !== 'none') {
        const height = element.offsetHeight;
        if (height > 400) { // Tall sections with bg images are likely heroes
          //this.debugLog('🚫 Detected hero section: tall with background image');
          return true;
        }
      }
  
      return false;
    }
  
    getIconHTML(textIcon, imageIcon, className) {
      // Prioritize image icon over text icon
      if (imageIcon) {
        return `<img src="${imageIcon}" class="${className}" style="width: 20px; height: 20px; object-fit: contain;" alt="Icon">`;
      } else if (textIcon) {
        return `<span class="${className}">${textIcon}</span>`;
      } else {
        return ''; // No icon
      }
    }
  
    debugLog(...args) {
      // Always log for now to help with debugging
    }
  }
  
  // Initialize when DOM is ready
  
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        new SidebarMenuSystem();
      }, 2000);
    });
  } else {
    setTimeout(() => {
      new SidebarMenuSystem();
    }, 2000);
  }
  
  // Global debug function
  window.debugSidebarSystem = function() {
    
    
    const system = new SidebarMenuSystem();
    const pageHandle = system.getCurrentPageHandle();
    
    if (pageHandle && window.SIDEBAR_MENU_SYSTEM) {
      const menuHandle = window.SIDEBAR_MENU_SYSTEM.pageMenuMap[pageHandle];
      
      if (menuHandle) {
        const menuData = window.SIDEBAR_MENU_SYSTEM.menus[menuHandle];
      }
    }
  }; 